import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import supabase from '@/lib/supabase';

interface SiteDetail {
  id: number;
  name: string;
  domain: string;
  status: 'safe' | 'warning' | 'scam';
  report_count: number;
  verified_date: string;
  category: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

const STATUS_CONFIG: Record<string, { label: string; icon: string; cardBg: string; badge: string; text: string; border: string }> = {
  safe: {
    label: '안전',
    icon: 'ri-shield-check-line',
    cardBg: 'bg-green-50',
    badge: 'bg-green-100 text-green-700',
    text: 'text-green-700',
    border: 'border-green-300',
  },
  warning: {
    label: '주의',
    icon: 'ri-error-warning-line',
    cardBg: 'bg-yellow-50',
    badge: 'bg-yellow-100 text-yellow-700',
    text: 'text-yellow-700',
    border: 'border-yellow-300',
  },
  scam: {
    label: '먹튀',
    icon: 'ri-close-circle-line',
    cardBg: 'bg-red-50',
    badge: 'bg-red-100 text-red-700',
    text: 'text-red-700',
    border: 'border-red-300',
  },
};

function formatFullDate(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}.${m}.${day} ${h}:${min}`;
  } catch {
    return dateStr;
  }
}

export default function SiteDetail() {
  const { id } = useParams<{ id: string }>();
  const [site, setSite] = useState<SiteDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchSite = async () => {
      if (!id) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const numericId = parseInt(id, 10);
        if (isNaN(numericId)) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('verified_sites')
          .select('*')
          .eq('id', numericId)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (!data) {
          setNotFound(true);
        } else {
          setSite(data as SiteDetail);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : '알 수 없는 오류';
        setError(message || '사이트 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchSite();
  }, [id]);

  const config = site ? STATUS_CONFIG[site.status] || STATUS_CONFIG.warning : null;

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Header />

      <main className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-40">
            <div className="text-center">
              <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-foreground-500">사이트 정보를 불러오는 중...</p>
            </div>
          </div>
        ) : error ? (
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-20">
            <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-2xl text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-red-700 mb-2">오류가 발생했습니다</h2>
              <p className="text-sm text-red-600 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line" /> 다시 시도
              </button>
            </div>
          </div>
        ) : notFound || !site ? (
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-20">
            <div className="bg-white border border-foreground-200 rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-2xl text-foreground-400" />
              </div>
              <h2 className="text-lg font-bold text-foreground-950 mb-2">사이트를 찾을 수 없습니다</h2>
              <p className="text-sm text-foreground-500 mb-4">요청하신 검증 사이트 정보가 존재하지 않거나 삭제되었습니다.</p>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm px-4 py-2 rounded-md transition-colors whitespace-nowrap"
              >
                <i className="ri-search-line" /> 검색 페이지로 이동
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className={`${config?.cardBg} border-b ${config?.border}`}>
              <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-500 transition-colors">홈</Link>
                  <i className="ri-arrow-right-s-line text-xs" />
                  <Link to="/search" className="hover:text-primary-500 transition-colors">검색</Link>
                  <i className="ri-arrow-right-s-line text-xs" />
                  <span className="text-foreground-950 font-medium">{site.name}</span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{site.name}</h1>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${config?.badge}`}>
                        <i className={`${config?.icon} text-xs mr-1`} />
                        {config?.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground-500">
                      <i className="ri-link text-sm" />
                      <a
                        href={`https://${site.domain}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-sm hover:text-primary-500 transition-colors underline underline-offset-2"
                      >
                        {site.domain}
                      </a>
                    </div>
                  </div>

                  <Link
                    to="/report"
                    className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm px-5 py-2.5 rounded-md transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-alert-line" /> 피해 제보하기
                  </Link>
                </div>
              </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="bg-white border border-foreground-200 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-background-100 rounded-lg flex items-center justify-center">
                      <i className="ri-price-tag-3-line text-foreground-600" />
                    </div>
                    <span className="text-xs text-foreground-500">분류</span>
                  </div>
                  <p className="text-base font-bold text-foreground-950">{site.category}</p>
                </div>

                <div className="bg-white border border-foreground-200 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-background-100 rounded-lg flex items-center justify-center">
                      <i className="ri-alert-line text-foreground-600" />
                    </div>
                    <span className="text-xs text-foreground-500">누적 신고</span>
                  </div>
                  <p className={`text-base font-bold ${(site.report_count || 0) >= 50 ? 'text-red-600' : 'text-foreground-950'}`}>
                    {(site.report_count || 0).toLocaleString()}건
                  </p>
                </div>

                <div className="bg-white border border-foreground-200 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-background-100 rounded-lg flex items-center justify-center">
                      <i className="ri-calendar-check-line text-foreground-600" />
                    </div>
                    <span className="text-xs text-foreground-500">검증일</span>
                  </div>
                  <p className="text-base font-bold text-foreground-950 whitespace-nowrap">
                    {site.verified_date ? formatFullDate(site.verified_date).split(' ')[0] : '-'}
                  </p>
                </div>

                <div className="bg-white border border-foreground-200 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-background-100 rounded-lg flex items-center justify-center">
                      <i className="ri-time-line text-foreground-600" />
                    </div>
                    <span className="text-xs text-foreground-500">마지막 업데이트</span>
                  </div>
                  <p className="text-sm font-bold text-foreground-950 whitespace-nowrap">
                    {formatFullDate(site.updated_at)}
                  </p>
                </div>
              </div>

              {site.tags && site.tags.length > 0 && (
                <div className="bg-white border border-foreground-200 rounded-lg p-5 mb-8">
                  <h3 className="text-sm font-bold text-foreground-950 mb-3">태그</h3>
                  <div className="flex gap-2 flex-wrap">
                    {site.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                          tag.includes('먹튀')
                            ? 'bg-red-50 text-red-600'
                            : tag.includes('안전')
                            ? 'bg-green-50 text-green-600'
                            : 'bg-yellow-50 text-yellow-600'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white border border-foreground-200 rounded-lg p-5 mb-8">
                <h3 className="text-sm font-bold text-foreground-950 mb-3">검증 결과</h3>
                <div className={`border rounded-lg p-4 ${config?.cardBg} ${config?.border}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${config?.badge}`}>
                      <i className={`${config?.icon} text-lg`} />
                    </div>
                    <div>
                      <p className={`text-sm font-bold ${config?.text} mb-1`}>
                        {site.status === 'safe'
                          ? '이 사이트는 안전한 것으로 검증되었습니다'
                          : site.status === 'warning'
                          ? '이 사이트는 주의가 필요합니다'
                          : '이 사이트는 먹튀 사이트로 확인되었습니다'}
                      </p>
                      <p className={`text-sm ${config?.text} opacity-80`}>
                        {site.status === 'safe'
                          ? `${site.name}(${site.domain})은(는) 현재까지 이상 징후가 발견되지 않았으며, 정상 운영 중인 것으로 확인되었습니다. 다만, 지속적인 모니터링이 필요합니다.`
                          : site.status === 'warning'
                          ? `${site.name}(${site.domain})은(는) 일부 이용자로부터 주의 신고가 접수되었습니다. 이용 시 각별한 주의가 필요하며, 거래 전 반드시 최신 정보를 확인하시기 바랍니다.`
                          : `${site.name}(${site.domain})은(는) 다수의 먹튀 신고가 접수되어 먹튀 사이트로 확정되었습니다. 절대 이용하지 마시고, 피해를 입으셨다면 즉시 제보해 주시기 바랍니다.`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-foreground-200 rounded-lg p-5">
                <h3 className="text-sm font-bold text-foreground-950 mb-3">사이트 기본 정보</h3>
                <div className="divide-y divide-foreground-200">
                  <div className="flex items-center py-3">
                    <span className="text-sm text-foreground-500 w-32 shrink-0">사이트명</span>
                    <span className="text-sm font-medium text-foreground-950">{site.name}</span>
                  </div>
                  <div className="flex items-center py-3">
                    <span className="text-sm text-foreground-500 w-32 shrink-0">도메인</span>
                    <a
                      href={`https://${site.domain}`}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors underline underline-offset-2"
                    >
                      {site.domain}
                    </a>
                  </div>
                  <div className="flex items-center py-3">
                    <span className="text-sm text-foreground-500 w-32 shrink-0">분류</span>
                    <span className="text-sm font-medium text-foreground-950">{site.category}</span>
                  </div>
                  <div className="flex items-center py-3">
                    <span className="text-sm text-foreground-500 w-32 shrink-0">검증 상태</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config?.badge}`}>
                      {config?.label}
                    </span>
                  </div>
                  <div className="flex items-center py-3">
                    <span className="text-sm text-foreground-500 w-32 shrink-0">누적 신고</span>
                    <span className={`text-sm font-medium ${(site.report_count || 0) >= 50 ? 'text-red-600' : 'text-foreground-950'}`}>
                      {(site.report_count || 0).toLocaleString()}건
                    </span>
                  </div>
                  <div className="flex items-center py-3">
                    <span className="text-sm text-foreground-500 w-32 shrink-0">검증일</span>
                    <span className="text-sm font-medium text-foreground-950">
                      {site.verified_date ? formatFullDate(site.verified_date).split(' ')[0] : '-'}
                    </span>
                  </div>
                  <div className="flex items-center py-3">
                    <span className="text-sm text-foreground-500 w-32 shrink-0">등록일</span>
                    <span className="text-sm font-medium text-foreground-950">
                      {formatFullDate(site.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}